/**
 * Products Search Hooks
 *
 * Hooks for searching raw products and POS products for autocomplete.
 */

import { useQuery } from '@tanstack/react-query';
import { rawProductsService } from '@/features/raw-products/services/raw-products.service';
import { posProductsService } from '@/features/pos-products/services/pos-products.service';
import type { SearchAutocompleteOption } from '@/components/ui';

/**
 * Hook to search raw products for autocomplete
 */
export function useRawProductsSearch(search: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['raw-products', 'search', search],
    queryFn: async () => {
      const result = await rawProductsService.list({
        search,
        limit: 20,
        page: 1,
      });
      // Map to autocomplete options (with unit for transfers)
      const options: (SearchAutocompleteOption & { unit?: string })[] = result.data.map((product) => ({
        id: product.id,
        label: product.name,
        sublabel: product.sku,
        unit: product.recipeUnit?.symbol,
      }));
      return options;
    },
    enabled: enabled && search.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to search POS products for autocomplete
 */
export function usePosProductsSearch(search: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['pos-products', 'search', search],
    queryFn: async () => {
      const result = await posProductsService.list({
        search,
        limit: 20,
        page: 1,
      });
      // Map to autocomplete options
      const options: SearchAutocompleteOption[] = result.items.map((product) => ({
        id: product.id,
        label: product.name,
        sublabel: product.posCategory || undefined,
      }));
      return options;
    },
    enabled: enabled && search.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}
